import {
  calendarWeeksAgo,
  incompleteFieldsFor,
  DEFAULT_WEEK_START_DAY,
  GAP_LIST_MAX_PER_MUSCLE,
  instancesKnownFromResults,
  proposeForVolumeGaps,
  sunkAt,
  TODAY_CANDIDATES_PER_MUSCLE,
  weeklyTargetActualStats,
  type CatalogMuscleSlug,
  type Exercise,
  type GapExerciseCandidate,
  type LocalizedText,
  type LocationUnavailable,
  type MuscleLandmarkReading,
  type TargetActualStats,
  type TimedSessionResult,
  type WeeklyTargetRow,
  type IncompleteSource,
} from "@heraion/shared";

// AP-9, the proposal half of the coverage row: for a muscle under MEV, which
// catalog exercises close the gap, and in how many sets. Part of the
// muscleCoverage view-model, kept in its own file so that one stays readable.
//
// The gap is MEV minus the average of the last 4 FULL weeks (Antenore, Q1),
// always in hard sets, even on a row whose target is in kg (§16.3).

/** Where the athlete trains by default and what they declared missing there.
 *  Absent for the coach: a place never reaches a coach. */
export type ProposalScope = {
  place: string | null;
  unavailable: LocationUnavailable | undefined;
};

/** No place, no filter: the whole catalog. What a coach reads. */
export const WHOLE_CATALOG: ProposalScope = { place: null, unavailable: undefined };

export type GapCandidateView = GapExerciseCandidate & { name: LocalizedText };

export type RowGapProposal =
  // Fewer than 4 full weeks of history: the average divides by 4 anyway, so
  // the gap would come out too big. Said, not proposed.
  | { kind: "shortHistory"; fullWeeks: number }
  | {
      kind: "proposal";
      mev: number;
      avgPerWeek: number;
      gap: number;
      /** The place the list is filtered on; null = whole catalog. */
      place: string | null;
      /** What the list shows (GAP_LIST_MAX_PER_MUSCLE). */
      candidates: GapCandidateView[];
      /** AP-9c: the longer ranking «Build today's session» reads, the list's
       *  candidates first. Absent: the list is all there is. */
      draftCandidates?: GapCandidateView[];
    };

/** AP-9c fill: a muscle with no MEV gap, still under MAV this week and on
 *  the 4-full-week average. Only the «today» draft reads it, to fill spare
 *  time; the Progress list never shows it. */
export type RowMavFill = {
  mav: number;
  avgPerWeek: number;
  /** Sets a week below MAV (the low end of the band's MAV range). */
  gap: number;
  candidates: GapCandidateView[];
};

/** D-b (01/10): a muscle under its band THIS week that neither the MEV
 *  proposal nor the MAV fill took, because its 4-week average is high. Only
 *  the «today» draft reads it (admit "week"); the Progress list never does. */
export type RowWeekFill = {
  target: "mev" | "mav";
  /** The band value of `target`. */
  targetSets: number;
  /** Sets below `targetSets` THIS week. */
  gap: number;
  candidates: GapCandidateView[];
};

const FULL_WEEKS = 4;

/** Full calendar weeks since the first session in hand, capped at 4. The
 *  results page is the newest 200 sessions, so an early cutoff only ever
 *  lands far past 4 weeks. */
export const fullWeeksOfHistory = (
  sessions: ReadonlyArray<TimedSessionResult>,
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number => {
  if (sessions.length === 0) return 0;
  const first = Math.min(...sessions.map((s) => s.startedAtMs));
  return Math.max(0, Math.min(FULL_WEEKS, calendarWeeksAgo(first, nowMs, weekStartDay)));
};

type HistoryCtx = {
  sessions: TimedSessionResult[];
  catalog: ReadonlyArray<Exercise>;
  index: ReadonlyMap<string, Exercise>;
  nowMs: number;
  weekStartDay?: number;
};

/** The 4-full-week average in HARD SETS for one muscle (Q1, §16.3). */
const avgHardSets = (ctx: HistoryCtx, muscle: string): number =>
  weeklyTargetActualStats(
    ctx.sessions,
    ctx.catalog,
    { axis: "muscle", key: muscle, metric: "hard-sets" },
    ctx.nowMs,
    ctx.weekStartDay,
    ctx.index,
  ).avgPerWeek;

/** The band is WEEKLY (the 15/09 rule, Antenore): every banded muscle with
 *  MEV > 0 that is under MEV this week and has no row yet gets a zero-set row,
 *  whatever its average. The 4-week trend decides only the proposal on it
 *  (`gapProposalsFor`). Shaped like the untargeted rows `computeWeeklyTargets`
 *  builds, so its 4-week stats are computed once, with the others. */
export const zeroSetRowsUnderMev = (
  readings: Iterable<MuscleLandmarkReading>,
  shown: ReadonlySet<string>,
  // #116: true = the week holds an uncatalogued exercise, so these zero rows
  // (the likeliest to be undercounted) carry the flag like every muscle
  // hard-set row. A source (a session with no bodyweight in the week): the
  // shared per-row rule decides, per muscle.
  incomplete: boolean | IncompleteSource = false,
): WeeklyTargetRow[] =>
  [...readings]
    .filter(
      (r) =>
        r.band !== undefined &&
        r.band.mev > 0 &&
        r.zone === "below-mev" &&
        !shown.has(r.muscle),
    )
    .map((r) => ({
      key: r.muscle,
      axis: "muscle" as const,
      metric: "hard-sets" as const,
      actual: r.weeklySets,
      target: null,
      bandLow: null,
      bandHigh: null,
      zone: null,
      noCoverage: false,
      prescribed: null,
      ...(typeof incomplete === "boolean"
        ? incomplete
          ? { incomplete: true }
          : {}
        : incompleteFieldsFor(incomplete, { axis: "muscle", key: r.muscle, metric: "hard-sets" })),
    }));

type ProposalInput = {
  row: WeeklyTargetRow;
  stats: TargetActualStats;
  landmark: MuscleLandmarkReading;
};

type ProposalCtx = HistoryCtx & { scope?: ProposalScope };

const avgOf = (e: ProposalInput, ctx: HistoryCtx): number =>
  e.row.metric === "hard-sets" ? e.stats.avgPerWeek : avgHardSets(ctx, e.row.key);

/** The ranked, named candidates per gap, index-aligned with `gaps`, on the
 *  athlete's place. One call per target: `alsoCovers` (a ranking key) reads
 *  the other gaps of the same call. */
const namedCandidates = (
  gaps: ReadonlyArray<{ muscle: CatalogMuscleSlug; weeklySets: number; gap: number }>,
  ctx: ProposalCtx,
): Array<{ muscle: CatalogMuscleSlug; gap: number; named: GapCandidateView[] }> => {
  const place = ctx.scope?.place ?? null;
  const sunk =
    place === null
      ? new Set<string>()
      : sunkAt(ctx.scope?.unavailable, place, instancesKnownFromResults(ctx.sessions));
  return proposeForVolumeGaps({
    gaps,
    catalog: ctx.catalog,
    isAvailable: (ex) => !sunk.has(ex.slug),
    maxPerMuscle: TODAY_CANDIDATES_PER_MUSCLE,
  }).map((p) => ({
    muscle: p.muscle,
    gap: p.gap,
    named: p.candidates.flatMap((c) => {
      const ex = ctx.index.get(c.slug);
      return ex ? [{ ...c, name: ex.name }] : [];
    }),
  }));
};

/** One proposal per row that asks for one, keyed by muscle. A row gets one
 *  only when BOTH this week and the 4-week average are under MEV (4b: a
 *  Tuesday under MEV with a full average above it has nothing to close). */
export const gapProposalsFor = (
  entries: ReadonlyArray<ProposalInput>,
  ctx: ProposalCtx,
): Map<string, RowGapProposal> => {
  const out = new Map<string, RowGapProposal>();
  const underThisWeek = entries.filter(
    (e) => e.landmark.band !== undefined && e.landmark.zone === "below-mev",
  );
  if (underThisWeek.length === 0) return out;

  const fullWeeks = fullWeeksOfHistory(ctx.sessions, ctx.nowMs, ctx.weekStartDay);
  if (fullWeeks < FULL_WEEKS) {
    for (const e of underThisWeek) {
      out.set(e.landmark.muscle, { kind: "shortHistory", fullWeeks });
    }
    return out;
  }

  const gaps: Array<{ muscle: CatalogMuscleSlug; weeklySets: number; gap: number; mev: number; avg: number }> = [];
  for (const e of underThisWeek) {
    const mev = e.landmark.band?.mev ?? 0;
    const avg = avgOf(e, ctx);
    if (avg >= mev) continue;
    gaps.push({ muscle: e.landmark.muscle, weeklySets: avg, gap: mev - avg, mev, avg });
  }
  if (gaps.length === 0) return out;

  const place = ctx.scope?.place ?? null;
  namedCandidates(gaps, ctx).forEach((p, i) => {
    const g = gaps[i];
    if (!g) return;
    out.set(p.muscle, {
      kind: "proposal",
      mev: g.mev,
      avgPerWeek: g.avg,
      gap: p.gap,
      place,
      candidates: p.named.slice(0, GAP_LIST_MAX_PER_MUSCLE),
      draftCandidates: p.named,
    });
  });
  return out;
};

/** AP-9c fill, keyed by muscle: every banded row under MAV this week AND on
 *  the 4-week average, with no MEV proposal (`mevProposals`). Needs 4 full
 *  weeks, like the MEV proposal. `ignoreAverage` (D-b, the «today» draft only)
 *  measures on this week alone: gap and `avgPerWeek` then hold this week's
 *  sets. `exclude` drops rows BEFORE the one naming call, so the ranking
 *  (`alsoCovers`, `maxPerMuscle`) sees only the rows that stay. The defaults
 *  are the Progress panel's rule. */
export const mavFillsFor = (
  entries: ReadonlyArray<ProposalInput>,
  ctx: ProposalCtx,
  mevProposals: ReadonlyMap<string, RowGapProposal>,
  opts: { ignoreAverage?: boolean; exclude?: ReadonlySet<string> } = {},
): Map<string, RowMavFill> => {
  const out = new Map<string, RowMavFill>();
  if (fullWeeksOfHistory(ctx.sessions, ctx.nowMs, ctx.weekStartDay) < FULL_WEEKS) return out;
  const gaps: Array<{ muscle: CatalogMuscleSlug; weeklySets: number; gap: number; mav: number; avg: number }> = [];
  for (const e of entries) {
    const band = e.landmark.band;
    if (!band || mevProposals.get(e.landmark.muscle)?.kind === "proposal") continue;
    if (opts.exclude?.has(e.landmark.muscle)) continue;
    const mav = band.mav[0];
    if (e.landmark.weeklySets >= mav) continue;
    const avg = opts.ignoreAverage ? e.landmark.weeklySets : avgOf(e, ctx);
    if (avg >= mav) continue;
    gaps.push({ muscle: e.landmark.muscle, weeklySets: avg, gap: mav - avg, mav, avg });
  }
  if (gaps.length === 0) return out;
  namedCandidates(gaps, ctx).forEach((p, i) => {
    const g = gaps[i];
    if (!g || p.named.length === 0) return;
    out.set(p.muscle, { mav: g.mav, avgPerWeek: g.avg, gap: p.gap, candidates: p.named });
  });
  return out;
};

/** D-b, keyed by muscle: the rows under their band THIS week that have neither
 *  a MEV proposal nor a MAV fill, whatever their average. The same walk as
 *  `mavFillsFor` with the average ignored and the MAV fills excluded up front,
 *  then each row is read against MEV first (under it this week) and MAV after. */
export const weekFillsFor = (
  entries: ReadonlyArray<ProposalInput>,
  ctx: ProposalCtx,
  mevProposals: ReadonlyMap<string, RowGapProposal>,
  mavFills: ReadonlyMap<string, RowMavFill>,
): Map<string, RowWeekFill> => {
  const out = new Map<string, RowWeekFill>();
  const week = new Map<string, MuscleLandmarkReading>(entries.map((e) => [e.landmark.muscle, e.landmark]));
  for (const [muscle, f] of mavFillsFor(entries, ctx, mevProposals, {
    ignoreAverage: true,
    exclude: new Set(mavFills.keys()),
  })) {
    const lm = week.get(muscle);
    if (!lm?.band) continue;
    const underMev = lm.band.mev > 0 && lm.weeklySets < lm.band.mev;
    out.set(muscle, {
      target: underMev ? "mev" : "mav",
      targetSets: underMev ? lm.band.mev : f.mav,
      gap: (underMev ? lm.band.mev : f.mav) - lm.weeklySets,
      candidates: f.candidates,
    });
  }
  return out;
};
